import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Banner from "../components/Banner";
import PrimeLayout from "../components/PrimeLayout";
import StyledSelect from "../components/elements/StyledSelect";
import StyledButton from "../components/elements/StyledButton";
import DatePicker from "../components/elements/DatePicker";
import MiniModal from "../components/MiniModal";
import ModelPermissionTable from "../components/ModelPermissionTable";
import Pagination from "../components/elements/Pagination";
import {
  DialogContentWrapper,
  IconWrapper,
  MiniModalContentTitle,
  MiniModalContentBody,
} from "../components/styled/ModelPermission.styled";
import { Grid } from "@mui/material";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { toast } from "react-toastify";
import {
  RequestStatusArray,
  ModelPermissionManagementTableHeaders,
} from "../constants/Data";
import { RequestStatus } from "../constants/RequestStatus";
import {
  approveUser,
  getTempRequests,
  patchRejectReason,
  rejectUser,
} from "../services/api/modelPermissions.api";
import moment from "moment";
import { BiSearchAlt } from "react-icons/bi";
import Footer from "../components/Footer";

export default function ModelPermissions() {
  const [requestStatus, setRequestStatus] = useState("init");
  const [requestedModel, setRequestedModel] = useState("init");
  const [requestedUser, setRequestedUser] = useState("init");
  const [order, setOrder] = useState({ id: "requested_date", sort: "asc" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("init");
  const [rejectReason, setRejectReason] = useState("init");
  const [dateRange, setDateRange] = useState([null, null]);
  const [page, setPage] = useState(1);
  const [requestRows, setRequestRows] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [userOptions, setUserOptions] = useState([]);
  const [requests, setRequests] = useState({});
  const [render, setRender] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [loading, setLoading] = useState(false);
  const [moduleName, setModuleName] = useState();
  const navigate = useNavigate();

  const getPage = (p) => {
    setPage(p);
  };

  const searchArray = async () => {
    try {
      setPage(1);
      setLoading(true);
      // obtain local UTC offset and convert to msec to T00:00:00.000Z --> dateRange[0].getTimezoneOffset() * 60000
      const dateFrom = dateRange[0]
        ? new Date(
            dateRange[0] - dateRange[0].getTimezoneOffset() * 60000
          ).toISOString()
        : null;
      // obtain local UTC offset and convert to msec to 23:59:00.000Z --> dateRange[1].getTimezoneOffset() * ((23 + ( 59/ 60 ) + 5.5 ) / 5.5) * 60000
      const dateTo = dateRange[1]
        ? new Date(
            dateRange[1] -
              dateRange[1].getTimezoneOffset() *
                ((23 + 59 / 60 + 5.5) / 5.5) *
                60000
          ).toISOString()
        : null;
      const newRequestRows = await getTempRequests({
        limit: 10,
        offset: page,
        key: order.id,
        order: order.sort,
        status: requestStatus === "init" ? null : requestStatus,
        template: requestedModel === "init" ? null : requestedModel,
        user: requestedUser === "init" ? null : requestedUser,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
      setRequests(newRequestRows);
      const rowData = newRequestRows.docs.map((doc) => {
        return {
          id: doc._id,
          user: doc.req_user.name,
          requested_date: moment(doc.requested_date).format(
            "DD/MM/YYYY, HH:mm"
          ),
          template: doc.template.name,
          status: doc.status,
          reason: doc.reason,
        };
      });
      setModelOptions(newRequestRows.models);
      setUserOptions(newRequestRows.users);
      setRequestRows([...rowData]);
      setLoading(false);
    } catch (err) {
      if (err.response)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  const patchReason = async () => {
    try {
      if (rejectReason === "init") {
        return toast.error("Please select a reason", { theme: "colored" });
      }
      setLoading(true);
      await rejectUser(selectedId);
      await patchRejectReason({
        id: selectedId,
        reason: rejectReason === "init" ? "User is not allowed" : rejectReason,
      });
      setRender(!render);
      setIsModalOpen(false);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  useEffect(() => {
    const getAllRequests = async () => {
      try {
        setLoading(true);
        const newRequestRows = await getTempRequests({
          limit: 10,
          offset: page,
          key: order.id,
          order: order.sort,
          status: requestStatus === "init" ? null : requestStatus,
          template: requestedModel === "init" ? null : requestedModel,
        });
        setRequests(newRequestRows);
        const rowData = newRequestRows.docs.map((doc) => {
          return {
            id: doc._id,
            user: doc.req_user.name,
            requested_date: moment(doc.requested_date).format(
              "DD/MM/YYYY, HH:mm"
            ),
            template: doc.template.name,
            status: doc.status,
            reason: doc.reason,
          };
        });
        setModelOptions(newRequestRows.models);
        setUserOptions(newRequestRows.users);
        setRequestRows([...rowData]);
        setLoading(false);
      } catch (err) {
        if (err.message === "No active account") {
          toast.error("Session Expired, Please Log back", {
            theme: "colored",
          });
          return navigate("/");
        }
      }
    };
    getAllRequests();
    // eslint-disable-next-line
  }, [order, page, render, navigate]);

  const onClickAction = async ({ data, type }) => {
    try {
      setLoading(true)
      setModuleName(data.template);
      if (type === "approved") {
        await approveUser(data.id);
      }
      if (type === "rejected") {
        setSelectedId(data.id);
      }
      setRender(!render);
      setModalType(type);
      setIsModalOpen(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      if (err.response)
        return toast.error(err.response.data.error.message, {
          theme: "colored",
        });
      toast.error(err.message, { theme: "colored" });
    }
  };

  return (
    <div>
      <Header />
      <Banner small />
      <PrimeLayout title={"Manage Module Permissions"}>
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Request Status"}
              defaultVal={"Select the Request Status"}
              options={RequestStatusArray}
              control={requestStatus}
              setControl={setRequestStatus}
            />
          </Grid>
          <Grid item xs={12} md={3} lg={2}>
            <StyledSelect
              label={"Requested Module"}
              defaultVal={"Select the Requested Module"}
              options={modelOptions}
              control={requestedModel}
              setControl={setRequestedModel}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <StyledSelect
              label={"Requested User"}
              defaultVal={"Select Requested User"}
              options={userOptions}
              control={requestedUser}
              setControl={setRequestedUser}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <DatePicker dateRange={dateRange} setDateRange={setDateRange} />
          </Grid>
          <Grid item md={6} xs={12}>
            <div style={{ margin: "0px", width: "fit-content" }}>
              <StyledButton
                iconLeft={<BiSearchAlt size={25} />}
                variant={"outlined"}
                style={{ marginTop: "25px" }}
                onClick={() => {
                  searchArray();
                }}
              >
                Search
              </StyledButton>
            </div>
          </Grid>
        </Grid>
        <ModelPermissionTable
          headCells={ModelPermissionManagementTableHeaders}
          rows={requestRows}
          order={order}
          setOrder={setOrder}
          loading={loading}
          chips={"status"}
          onClickAction={onClickAction}
        />
        {requests.totalPages > 1 && !loading && (
          <Pagination
            count={requests.totalPages}
            getPage={getPage}
            page={page}
          />
        )}

        <MiniModal
          title={
            modalType === RequestStatus.APPROVED
              ? "Request Approved"
              : "Request Rejected"
          }
          open={isModalOpen}
          setOpenModal={setIsModalOpen}
          onClose={() => {
            setIsModalOpen(false);
          }}
          isFeedback={modalType === RequestStatus.APPROVED ? false : true}
          feedback={patchReason}
          confirmationLabel={"OK"}
          loading={loading}
        >
          {modalType === RequestStatus.APPROVED ? (
            <DialogContentWrapper>
              <IconWrapper type={"success"}>
                <IoCheckmarkCircleOutline />
              </IconWrapper>
              <MiniModalContentTitle type={"success"}>
                Module access request for {moduleName} successfully approved
              </MiniModalContentTitle>
            </DialogContentWrapper>
          ) : (
            <DialogContentWrapper>
              <IconWrapper type={"error"}>
                <AiOutlineCloseCircle />
              </IconWrapper>
              <MiniModalContentTitle type={"error"}>
                Module access request for {moduleName} Rejected
              </MiniModalContentTitle>
              <MiniModalContentBody>
                <StyledSelect
                  label={"Request Reason"}
                  defaultVal={"Select Reject Reason"}
                  options={[
                    { label: "You are not authorize to access this data" },
                    { label: "You are not authorize to access Country data" },
                  ]}
                  control={rejectReason}
                  setControl={setRejectReason}
                />
              </MiniModalContentBody>
            </DialogContentWrapper>
          )}
        </MiniModal>
      </PrimeLayout>
      <Footer/>
    </div>
  );
}
